import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SEO } from "../components/SEO";
import { useLocalePath, useTranslation } from "@thyra/landing-i18n";

const SUGGESTED_LINKS = [
  { key: "product", to: "/#product" },
  { key: "pricing", to: "/#pricing" },
  { key: "about", to: "/about" },
  { key: "contact", to: "/contact" },
] as const;

export default function NotFoundPage() {
  const { t } = useTranslation(["common", "seo"]);
  const lp = useLocalePath();
  return (
    <div className="min-h-screen bg-background">
      {/* canonicalPath pins the canonical to /404: the real URL is unknown and
          would otherwise canonicalize every bad path to itself. */}
      <SEO
        title={t("seo:notFound.title")}
        description={t("seo:notFound.description")}
        canonicalPath="/404"
        noindex
      />
      <Header />
      <main className="px-4 pb-16 pt-24 sm:px-6">
        <section
          className="mx-auto flex w-full max-w-3xl flex-col items-center text-center"
          data-aos="fade-up"
        >
          {/* .eyebrow-pill supplies its own pulsing ::before dot, so no icon. */}
          <span className="eyebrow-pill">{t("notFound.eyebrow")}</span>

          <p className="heading-gradient mt-6 text-7xl font-bold leading-none tracking-tight sm:text-8xl">
            404
          </p>

          <h1 className="mt-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t("notFound.title")}
          </h1>

          <p className="mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
            {t("notFound.body")}
          </p>

          <div className="mt-8 flex w-full max-w-md flex-col items-stretch gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center">
            <Link
              to={lp("/")}
              className="btn-brand group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem]"
            >
              {t("notFound.home")}
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                strokeWidth={2}
              />
            </Link>
            <Link
              to={lp("/contact")}
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-border bg-background/80 px-7 text-sm font-semibold text-foreground shadow-sm backdrop-blur-sm transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem] dark:bg-zinc-900/70"
            >
              {t("notFound.contact")}
            </Link>
          </div>

          <div className="mt-12 w-full border-t border-border pt-8">
            <p className="text-sm font-medium text-foreground">
              {t("notFound.popular")}
            </p>
            <ul className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {SUGGESTED_LINKS.map(({ key, to }) => (
                <li key={to}>
                  <Link
                    to={lp(to)}
                    className="inline-flex h-9 items-center rounded-lg border border-border bg-background/80 px-4 text-sm text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  >
                    {t(`nav.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
